import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronRight, Database, Download, Eye, EyeOff, Lock, Play, RefreshCw, Search, Sparkles, Trash2, Upload, X } from 'lucide-react';
import type { PageComponentProps } from '../../../registry/react-component-registry.constants';
import type { SandboxDataset, SandboxJourneysResponse, SandboxRun, SandboxFile, SandboxDatasetAPI, SandboxRunAPI, SandboxRunItemAPI } from '../../../types/analytics-sandbox';
import { analyticsSandboxAPI } from '../../../services/analytics-sandbox-api.service';
import { SandboxDatasetsDrawer } from './SandboxDatasetsDrawer';
import { Button, Tag, InputField, Dropdown, DropdownTrigger, DropdownContent, DropdownItem } from 'equal-ds-ui';

// ── Helpers ────────────────────────────────────────────────────────────────────
function fmtDate(isoStr?: string, short?: boolean) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const datePart = short
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return datePart + ', ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

// Sequelize raw:true returns timestamps as camelCase (createdAt) not snake_case (created_at)
function getCreatedAt(api: any): string | undefined {
  return api.created_at || api.createdAt;
}

function toDisplayDataset(api: SandboxDatasetAPI): SandboxDataset {
  return {
    id: api.id,
    name: api.name,
    fileCount: api.file_count,
    files: (api.files || []).map(f => ({ name: f.source_ref })),
    consents: api.consents || [],
    runCount: 0,
    createdAt: fmtDate(getCreatedAt(api)),
  };
}

function toDisplayRun(api: SandboxRunAPI, datasetName: string): SandboxRun {
  const id = api.id;
  return {
    id,
    shortId: `${id.slice(0, 4)}...${id.slice(-4)}`,
    datasetId: api.dataset_id,
    datasetName,
    status: api.status,
    totalItems: api.total_items ?? null,
    completed: api.completed ?? 0,
    failed: api.failed ?? 0,
    createdAt: fmtDate(getCreatedAt(api), true),
    transactionRef: api.transaction_ref ?? null,
  };
}

/** Detect which files in a list are password-protected PDFs */
async function detectProtectedFiles(files: File[]): Promise<Set<string>> {
  const results = await Promise.all(
    files.map(async f => {
      try {
        const buf  = await f.arrayBuffer();
        const text = new TextDecoder('latin1').decode(new Uint8Array(buf));
        return /\/Encrypt[\s/<[]/.test(text) ? f.name : null;
      } catch {
        return null;
      }
    })
  );
  return new Set(results.filter(Boolean) as string[]);
}

/** Append (1), (2) … to name if it already exists in the datasets list */
function uniqueName(name: string, existing: SandboxDataset[]): string {
  const taken = new Set(existing.map(d => d.name.toLowerCase()));
  if (!taken.has(name.toLowerCase())) return name;
  let n = 1;
  while (taken.has(`${name} (${n})`.toLowerCase())) n++;
  return `${name} (${n})`;
}

// ── Status badge helpers ───────────────────────────────────────────────────────
const STATUS_TAG_CLASS: Record<string, string> = {
  COMPLETED:  'bg-green-100 text-green-700 rounded-full',
  PROCESSING: 'bg-amber-100 text-amber-700 rounded-full',
  PARTIAL:    'bg-orange-100 text-orange-800 rounded-full',
  FAILED:     'bg-red-100 text-red-700 rounded-full',
};

const PDF_STATUS_TAG_CLASS: Record<string, string> = {
  SUCCESS: 'bg-green-100 text-green-700 rounded-full',
  PENDING: 'bg-amber-100 text-amber-700 rounded-full',
  FAILED:  'bg-red-100 text-red-700 rounded-full',
};


async function downloadRunZIP(runId: string, setDownloading: (v: boolean) => void, setError: (v: string | null) => void) {
  setDownloading(true);
  setError(null);
  try {
    const { job_id } = await analyticsSandboxAPI.downloadRunResults(runId);
    let downloadUrl: string | null = null;
    for (let i = 0; i < 30; i++) {
      await new Promise(r => setTimeout(r, 3000));
      const status = await analyticsSandboxAPI.getDownloadJobStatus(job_id);
      if (status.status === 'SUCCESS') {
        downloadUrl = status.download_url || await analyticsSandboxAPI.getJobDownloadUrl(job_id);
        break;
      }
      if (status.status === 'FAILED') throw new Error(status.error || 'Download job failed');
    }
    if (!downloadUrl) throw new Error('Download timed out');
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `results_${runId}.zip`;
    a.click();
  } catch (err: any) {
    setError(err?.message || 'Download failed');
  } finally {
    setDownloading(false);
  }
}

// ── Session state persistence ───────────────────────────────────────────────────
const SANDBOX_STATE_KEY = 'analytics_sandbox_state';
interface SandboxPersistedState { filterDataset: string; filterStatus: string; currentPage: number; }
function loadSandboxState(): SandboxPersistedState | null {
  try { const r = sessionStorage.getItem(SANDBOX_STATE_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}
function saveSandboxState(s: SandboxPersistedState) {
  try { sessionStorage.setItem(SANDBOX_STATE_KEY, JSON.stringify(s)); } catch {}
}

// Shared inline input style for filter bar (matches DropdownTrigger outline height/look)
const filterInputStyle: React.CSSProperties = {
  fontSize: 13, height: 32, padding: '0 28px 0 28px',
  border: '1px solid #d1d5db', borderRadius: 6, outline: 'none',
  width: 200, color: '#374151', background: '#fff',
  boxSizing: 'border-box',
};

// ── Main Component ─────────────────────────────────────────────────────────────

export const AnalyticsSandboxPage: React.FC<PageComponentProps> = () => {
  // Data state
  const [datasets, setDatasets] = useState<SandboxDataset[]>([]);
  const [runs, setRuns]         = useState<SandboxRun[]>([]);
  const [journeys, setJourneys] = useState<SandboxJourneysResponse>({ pdf: [], consent: [] });
  const maxPdfItems     = journeys.limits?.max_pdf_items     ?? 10;
  const maxConsentItems = journeys.limits?.max_consent_items ?? 10;

  // Inline run expansion
  const [expandedRuns, setExpandedRuns]           = useState<Set<string>>(new Set());
  const [runResults, setRunResults]               = useState<Record<string, SandboxRunItemAPI[]>>({});
  const [runResultsLoading, setRunResultsLoading] = useState<Set<string>>(new Set());
  const [runResultsTotals, setRunResultsTotals]   = useState<Record<string, number>>({});
  const [runResultsPages, setRunResultsPages]     = useState<Record<string, number>>({});
  const RESULTS_PAGE_SIZE = 50;

  const [refreshing, setRefreshing] = useState(false);
  const [runDownloading, setRunDownloading] = useState<Record<string, boolean>>({});
  const [runDownloadError, setRunDownloadError] = useState<Record<string, string | null>>({});

  // Filters — restored from sessionStorage
  const [filterDataset, setFilterDataset] = useState(() => loadSandboxState()?.filterDataset ?? '');
  const [filterStatus, setFilterStatus]   = useState(() => loadSandboxState()?.filterStatus  ?? '');
  // Transaction ref search: txnRefDraft is the live input value; filterTransactionRef is committed on Enter
  const [txnRefDraft, setTxnRefDraft]                 = useState('');
  const [filterTransactionRef, setFilterTransactionRef] = useState('');
  // Transaction ref for the new-run modal
  const [runTransactionRef, setRunTransactionRef] = useState('');

  // Pagination
  const PAGE_SIZE = 20;
  const [currentPage, setCurrentPage] = useState(() => loadSandboxState()?.currentPage ?? 1);
  const [totalRuns, setTotalRuns]     = useState(0);

  // Modal state
  const [showDatasetsDrawer, setShowDatasetsDrawer]         = useState(false);
  const [showNewRunModal, setShowNewRunModal]                = useState(false);
  const [showNewDatasetModal, setShowNewDatasetModal]        = useState(false);
  const [showUploadModal, setShowUploadModal]                = useState(false);
  const [showDeleteDatasetModal, setShowDeleteDatasetModal]  = useState(false);
  const [showRunConfirmModal, setShowRunConfirmModal]        = useState(false);

  // Pending actions
  const [pendingUploadDatasetId, setPendingUploadDatasetId] = useState('');
  const [pendingDeleteDataset, setPendingDeleteDataset]     = useState<SandboxDataset | null>(null);
  const [newDatasetName, setNewDatasetName]                 = useState('');
  const [newDatasetFiles, setNewDatasetFiles]               = useState<File[]>([]);
  const [newDatasetPasswords, setNewDatasetPasswords]       = useState<Record<string, string>>({});
  const [newDsProtectedFiles, setNewDsProtectedFiles]       = useState<Set<string>>(new Set());
  const [newDsPwdVisible, setNewDsPwdVisible]               = useState<Set<string>>(new Set());
  const [newDatasetConsentInput, setNewDatasetConsentInput] = useState('');
  const [createError, setCreateError]                       = useState<string | null>(null);
  const [deleteDatasetError, setDeleteDatasetError]         = useState<string | null>(null);
  const [fileDeleteError, setFileDeleteError]               = useState<string | null>(null);
  const [uploadError, setUploadError]                       = useState<string | null>(null);
  const [showDeleteFileModal, setShowDeleteFileModal]        = useState(false);
  const [pendingDeleteFile, setPendingDeleteFile]            = useState<{ datasetId: string; file: SandboxFile; datasetName: string } | null>(null);
  const [runDatasetId, setRunDatasetId]                     = useState('');
  const [runPdfJourneyId, setRunPdfJourneyId]               = useState('');
  const [runConsentJourneyId, setRunConsentJourneyId]       = useState('');
  const [runDatasetDetail, setRunDatasetDetail]             = useState<{ files: string[]; consents: string[] } | null>(null);
  const [runFieldErrors, setRunFieldErrors]                 = useState({ dataset: false, journey: false });
  const [triggerRunError, setTriggerRunError]               = useState<string | null>(null);
  const [uploadFiles, setUploadFiles]                       = useState<File[]>([]);
  const [uploadPasswords, setUploadPasswords]               = useState<Record<string, string>>({});
  const [uploadProtectedFiles, setUploadProtectedFiles]     = useState<Set<string>>(new Set());
  const [uploadPwdVisible, setUploadPwdVisible]             = useState<Set<string>>(new Set());
  const newDsFileInputRef  = useRef<HTMLInputElement>(null);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);

  // Derived: content types available in the loaded run dataset detail
  const selectedDsHasPdfs     = !!runDatasetDetail && runDatasetDetail.files.length > 0;
  const selectedDsHasConsents = !!runDatasetDetail && runDatasetDetail.consents.length > 0;

  // Refs for polling — avoid stale closures without re-creating the interval
  const runsRef         = useRef(runs);
  const datasetsRef     = useRef(datasets);
  const filterPageRef   = useRef({ page: currentPage, datasetId: filterDataset, status: filterStatus, transactionRef: filterTransactionRef });
  const expandedRunsRef = useRef(expandedRuns);
  useEffect(() => { runsRef.current = runs; },             [runs]);
  useEffect(() => { datasetsRef.current = datasets; },     [datasets]);
  useEffect(() => { expandedRunsRef.current = expandedRuns; }, [expandedRuns]);
  useEffect(() => { filterPageRef.current = { page: currentPage, datasetId: filterDataset, status: filterStatus, transactionRef: filterTransactionRef }; }, [currentPage, filterDataset, filterStatus, filterTransactionRef]);

  // Persist filter/page state across navigation
  useEffect(() => { saveSandboxState({ filterDataset, filterStatus, currentPage }); }, [filterDataset, filterStatus, currentPage]);

  // ── Inject styles into document head (overrides Bootstrap !important rules) ──
  useEffect(() => {
    const el = document.createElement('style');
    el.id = 'sandbox-page-styles';
    el.innerHTML = SANDBOX_STYLES;
    if (!document.getElementById('sandbox-page-styles')) {
      document.head.appendChild(el);
    }
    return () => { document.getElementById('sandbox-page-styles')?.remove(); };
  }, []);

  // ── Server-side run loader ────────────────────────────────────────────────
  const loadRuns = useCallback(async (page: number, datasetId: string, status: string, transactionRef?: string) => {
    const { runs: apiRuns, total } = await analyticsSandboxAPI.getRuns({
      page, limit: PAGE_SIZE,
      datasetId: datasetId || undefined,
      status: status || undefined,
      transactionRef: transactionRef || undefined,
    });
    const dsMap = new Map(datasetsRef.current.map(d => [d.id, d]));
    setRuns(apiRuns.map(r => { const ds = dsMap.get(r.dataset_id); return toDisplayRun(r, ds?.name || r.dataset_id); }));
    setTotalRuns(total);
  }, []);

  // ── Initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { page, datasetId, status, transactionRef } = filterPageRef.current;
        const [apiDatasets, { runs: apiRuns, total }, apiJourneys] = await Promise.all([
          analyticsSandboxAPI.getDatasets(),
          analyticsSandboxAPI.getRuns({ page, limit: PAGE_SIZE, datasetId: datasetId || undefined, status: status || undefined, transactionRef: transactionRef || undefined }),
          analyticsSandboxAPI.getJourneys(),
        ]);
        if (cancelled) return;
        const dsMap = new Map(apiDatasets.map(d => [d.id, d]));
        const sortedDs = [...apiDatasets].sort((a, b) =>
          new Date(getCreatedAt(b) || 0).getTime() - new Date(getCreatedAt(a) || 0).getTime()
        );
        const runCountMap = new Map<string, number>(sortedDs.map(d => [d.id, 0]));
        apiRuns.forEach((r: any) => { const c = runCountMap.get(r.dataset_id); if (c !== undefined) runCountMap.set(r.dataset_id, c + 1); });
        setJourneys(apiJourneys);
        setDatasets(sortedDs.map(d => ({ ...toDisplayDataset(d), runCount: runCountMap.get(d.id) || 0 })));
        setRuns(apiRuns.map(r => { const ds = dsMap.get(r.dataset_id); return toDisplayRun(r, ds?.name || r.dataset_id); }));
        setTotalRuns(total);
      } catch (err) {
        console.error('Failed to load sandbox data', err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Poll run statuses while any run is PROCESSING ────────────────────────
  useEffect(() => {
    const id = setInterval(async () => {
      if (!runsRef.current.some(r => r.status === 'PROCESSING')) return;
      try {
        const { page, datasetId, status, transactionRef } = filterPageRef.current;
        const { runs: apiRuns } = await analyticsSandboxAPI.getRuns({ page, limit: PAGE_SIZE, datasetId: datasetId || undefined, status: status || undefined, transactionRef: transactionRef || undefined });
        const dsMap = new Map(datasetsRef.current.map(d => [d.id, d]));
        const updated = apiRuns.map(r => {
          const ds = dsMap.get(r.dataset_id);
          return toDisplayRun(r, ds?.name || r.dataset_id);
        });
        setRuns(updated);
        // Clear cached results for runs that just left PROCESSING so they re-fetch on expand
        const nowDone = new Set(
          updated.filter(r => r.status !== 'PROCESSING').map(r => r.id)
        );
        setRunResults(prev => {
          const next = { ...prev };
          runsRef.current
            .filter(r => r.status === 'PROCESSING' && nowDone.has(r.id))
            .forEach(r => { delete next[r.id]; });
          return next;
        });
        // Clear stale download errors for runs that just completed processing
        setRunDownloadError(prev => {
          const next = { ...prev };
          runsRef.current
            .filter(r => r.status === 'PROCESSING' && nowDone.has(r.id))
            .forEach(r => { delete next[r.id]; });
          return next;
        });
        // Re-fetch results immediately for runs that completed while still expanded
        runsRef.current
          .filter(r => r.status === 'PROCESSING' && nowDone.has(r.id) && expandedRunsRef.current.has(r.id))
          .forEach(async r => {
            try {
              const { items, total } = await analyticsSandboxAPI.getRunResults(r.id, 1, RESULTS_PAGE_SIZE);
              setRunResults(prev => ({ ...prev, [r.id]: items }));
              setRunResultsTotals(t => ({ ...t, [r.id]: total }));
              setRunResultsPages(p => ({ ...p, [r.id]: 1 }));
            } catch (e) { console.warn('Failed to refresh results for run', r.id, e); }
          });
      } catch {
        // silent — will retry next tick
      }
    }, 5000);
    return () => clearInterval(id);
  }, []); // set up once; reads live data via refs

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleFilterDataset = useCallback((val: string) => {
    setFilterDataset(val); setCurrentPage(1); loadRuns(1, val, filterPageRef.current.status, filterPageRef.current.transactionRef);
  }, [loadRuns]);

  const handleFilterStatus = useCallback((val: string) => {
    setFilterStatus(val); setCurrentPage(1); loadRuns(1, filterPageRef.current.datasetId, val, filterPageRef.current.transactionRef);
  }, [loadRuns]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page); loadRuns(page, filterPageRef.current.datasetId, filterPageRef.current.status, filterPageRef.current.transactionRef);
  }, [loadRuns]);

  const handleSearchTxnRef = useCallback((val: string) => {
    setFilterTransactionRef(val); setCurrentPage(1); loadRuns(1, filterPageRef.current.datasetId, filterPageRef.current.status, val);
  }, [loadRuns]);

  const navigateToOnboarding = useCallback(() => {
    const bridge = (window as any).AngularAuthBridge;
    if (bridge?.navigate) {
      bridge.navigate('/dashboard/analytics-onboarding');
    } else {
      window.location.href = '/dashboard/analytics-onboarding';
    }
  }, []);

  const loadRunResultsPage = useCallback(async (runId: string, page: number) => {
    setRunResultsLoading(l => new Set([...l, runId]));
    try {
      const { items, total } = await analyticsSandboxAPI.getRunResults(runId, page, RESULTS_PAGE_SIZE);
      setRunResults(r => ({ ...r, [runId]: items }));
      setRunResultsTotals(t => ({ ...t, [runId]: total }));
      setRunResultsPages(p => ({ ...p, [runId]: page }));
    } catch {
      setRunResults(r => ({ ...r, [runId]: [] }));
    } finally {
      setRunResultsLoading(l => { const s = new Set(l); s.delete(runId); return s; });
    }
  }, []);

  const toggleExpandRun = useCallback((runId: string) => {
    const isExpanding = !expandedRuns.has(runId);
    setExpandedRuns(prev => {
      const next = new Set(prev);
      isExpanding ? next.add(runId) : next.delete(runId);
      return next;
    });
    if (isExpanding && !(runId in runResults)) {
      loadRunResultsPage(runId, 1);
    }
  }, [expandedRuns, runResults, loadRunResultsPage]);

  const handleCreateDataset = async () => {
    if (!newDatasetName.trim()) {
      setCreateError('Please enter a dataset name');
      return;
    }
    setCreateError(null);
    const finalName = uniqueName(newDatasetName.trim(), datasets);
    try {
      const apiDs = await analyticsSandboxAPI.createDataset(finalName);
      const consentIds = newDatasetConsentInput.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
      await Promise.all([
        ...(newDatasetFiles.length > 0
          ? newDatasetFiles.map(f => analyticsSandboxAPI.uploadFile(apiDs.id, f, newDatasetPasswords[f.name]))
          : []),
        ...(consentIds.length > 0
          ? [analyticsSandboxAPI.addConsents(apiDs.id, consentIds)]
          : []),
      ]);
      setDatasets(prev => [{
        id: apiDs.id,
        name: apiDs.name,
        fileCount: newDatasetFiles.length,
        files: newDatasetFiles.map(f => ({ name: f.name })),
        consents: consentIds,
        runCount: 0,
        createdAt: fmtDate(apiDs.created_at),
      }, ...prev]);
      setNewDatasetName('');
      setNewDatasetFiles([]);
      setNewDatasetPasswords({});
      setNewDsProtectedFiles(new Set());
      setNewDatasetConsentInput('');
      setCreateError(null);
      setShowNewDatasetModal(false);
      setShowDatasetsDrawer(true);
    } catch (err: any) {
      console.error('Failed to create dataset', err);
      setCreateError(err?.response?.data?.message || err?.message || 'Failed to create dataset. Please try again.');
    }
  };

  const handleDeleteDataset = (ds: SandboxDataset) => {
    setPendingDeleteDataset(ds);
    setShowDeleteDatasetModal(true);
  };

  const confirmDeleteDataset = async () => {
    if (!pendingDeleteDataset) return;
    setDeleteDatasetError(null);
    try {
      await analyticsSandboxAPI.deleteDataset(pendingDeleteDataset.id);
      setDatasets(prev => prev.filter(d => d.id !== pendingDeleteDataset.id));
      setRuns(prev => prev.filter(r => r.datasetId !== pendingDeleteDataset.id));
      setPendingDeleteDataset(null);
      setDeleteDatasetError(null);
      setShowDeleteDatasetModal(false);
    } catch (err: any) {
      console.error('Failed to delete dataset', err);
      setDeleteDatasetError(err?.response?.data?.message || err?.message || 'Failed to delete dataset. Please try again.');
    }
  };

  const handleDeleteFile = (datasetId: string, file: SandboxFile) => {
    const ds = datasets.find(d => d.id === datasetId);
    setPendingDeleteFile({ datasetId, file, datasetName: ds?.name || datasetId });
    setFileDeleteError(null);
    setShowDeleteFileModal(true);
  };

  const confirmDeleteFile = async () => {
    if (!pendingDeleteFile) return;
    setFileDeleteError(null);
    try {
      await analyticsSandboxAPI.deleteFile(pendingDeleteFile.datasetId, pendingDeleteFile.file.name);
      setDatasets(prev => prev.map(d =>
        d.id === pendingDeleteFile.datasetId ? {
          ...d,
          files: d.files.filter(f => f.name !== pendingDeleteFile.file.name),
          fileCount: Math.max(0, (d.fileCount ?? d.files.length) - 1),
        } : d
      ));
      setPendingDeleteFile(null);
      setShowDeleteFileModal(false);
    } catch (err: any) {
      console.error('Failed to delete file', err);
      setFileDeleteError(err?.response?.data?.message || err?.message || 'Failed to delete file. Please try again.');
    }
  };

  const handleUploadFilesForDataset = (datasetId: string) => {
    setPendingUploadDatasetId(datasetId);
    setUploadFiles([]);
    setUploadPasswords({});
    setUploadProtectedFiles(new Set());
    setUploadError(null);
    setShowUploadModal(true);
  };

  const handleConfirmUpload = async () => {
    if (!uploadFiles.length) { setShowUploadModal(false); setUploadProtectedFiles(new Set()); setUploadError(null); return; }
    setUploadError(null);
    try {
      await Promise.all(
        uploadFiles.map(f => analyticsSandboxAPI.uploadFile(pendingUploadDatasetId, f, uploadPasswords[f.name]))
      );
      const newFiles: SandboxFile[] = uploadFiles.map(f => ({ name: f.name }));
      setDatasets(prev => prev.map(d =>
        d.id === pendingUploadDatasetId ? {
          ...d,
          files: [...d.files, ...newFiles],
          fileCount: (d.fileCount ?? d.files.length) + uploadFiles.length,
        } : d
      ));
      setUploadFiles([]);
      setUploadPasswords({});
      setUploadProtectedFiles(new Set());
      setShowUploadModal(false);
    } catch (err: any) {
      setUploadError(err?.response?.data?.message || err?.message || 'Failed to upload files. Please try again.');
    }
  };

  const handleTriggerRun = () => {
    const dsErr = !runDatasetId;
    let jErr = false;
    if (runDatasetDetail) {
      if (selectedDsHasPdfs && !runPdfJourneyId) jErr = true;
      if (selectedDsHasConsents && !runConsentJourneyId) jErr = true;
      if (!selectedDsHasPdfs && !selectedDsHasConsents) jErr = true;
    } else {
      jErr = true;
    }
    setRunFieldErrors({ dataset: dsErr, journey: jErr });
    if (dsErr || jErr) return;

    if (runDatasetDetail && runDatasetDetail.files.length > maxPdfItems) {
      setTriggerRunError(`This dataset has ${runDatasetDetail.files.length} PDFs — the run limit is ${maxPdfItems} per run.`);
      return;
    }
    if (runDatasetDetail && runDatasetDetail.consents.length > maxConsentItems) {
      setTriggerRunError(`This dataset has ${runDatasetDetail.consents.length} consents — the run limit is ${maxConsentItems} per run.`);
      return;
    }

    setTriggerRunError(null);
    setShowRunConfirmModal(true);
  };

  const confirmTriggerRun = async () => {
    const ds = datasets.find(d => d.id === runDatasetId);
    if (!ds || !runDatasetDetail) return;
    setTriggerRunError(null);
    const items = [
      ...runDatasetDetail.files.map(f => ({ source_ref: f, item_type: 'PDF' as const, journey_id: runPdfJourneyId })),
      ...runDatasetDetail.consents.map(c => ({ source_ref: c, item_type: 'CONSENT' as const, journey_id: runConsentJourneyId })),
    ];
    try {
      await analyticsSandboxAPI.triggerRun({ dataset_id: runDatasetId, items, transaction_ref: runTransactionRef || undefined });
      setCurrentPage(1);
      await loadRuns(1, filterPageRef.current.datasetId, filterPageRef.current.status, filterPageRef.current.transactionRef);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to trigger run';
      setTriggerRunError(msg);
      return;
    }
    setRunDatasetId('');
    setRunPdfJourneyId('');
    setRunConsentJourneyId('');
    setRunDatasetDetail(null);
    setRunFieldErrors({ dataset: false, journey: false });
    setRunTransactionRef('');
    setTriggerRunError(null);
    setShowRunConfirmModal(false);
    setShowNewRunModal(false);
  };

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const { runs: apiRuns, total } = await analyticsSandboxAPI.getRuns({ page: filterPageRef.current.page, limit: PAGE_SIZE, datasetId: filterPageRef.current.datasetId || undefined, status: filterPageRef.current.status || undefined, transactionRef: filterPageRef.current.transactionRef || undefined });
      const dsMap = new Map(datasets.map(d => [d.id, d]));
      setRuns(apiRuns.map((r: SandboxRunAPI) => {
        const ds = dsMap.get(r.dataset_id);
        return toDisplayRun(r, ds?.name || r.dataset_id);
      }));
      setTotalRuns(total);
      // Clear cached results so expanded rows reload
      setRunResults({});
    } catch (err) {
      console.error('Failed to refresh runs', err);
    } finally {
      setRefreshing(false);
    }
  }, [datasets]);

  const handleLoadDatasetFiles = useCallback(async (datasetId: string) => {
    try {
      const apiDs = await analyticsSandboxAPI.getDataset(datasetId);
      setDatasets(prev => prev.map(d =>
        d.id === datasetId ? {
          ...d,
          files: (apiDs.files || []).map(f => ({ name: f.source_ref })),
          fileCount: apiDs.file_count,
          consents: apiDs.consents || [],
        } : d
      ));
    } catch (err) {
      console.error('Failed to load dataset files', err);
    }
  }, []);

  const handleAddConsents = useCallback(async (datasetId: string, consentIds: string[]) => {
    await analyticsSandboxAPI.addConsents(datasetId, consentIds);
    setDatasets(prev => prev.map(d =>
      d.id === datasetId ? {
        ...d,
        consents: [...new Set([...d.consents, ...consentIds])],
      } : d
    ));
  }, []);

  const handleRemoveConsent = useCallback(async (datasetId: string, consentId: string) => {
    await analyticsSandboxAPI.removeConsent(datasetId, consentId);
    setDatasets(prev => prev.map(d =>
      d.id === datasetId ? { ...d, consents: d.consents.filter(c => c !== consentId) } : d
    ));
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="sb-root equal-ds-root sb-page" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: 0 }}>

        {/* Page header */}
        <div style={{ marginBottom: 24 }}>
          <h1 className="text-2xl font-semibold text-text-primary tracking-wide" style={{ margin: '0 0 4px' }}>Analytics Sandbox</h1>
          <p className="text-sm text-text-secondary" style={{ margin: 0 }}>Trigger runs against PDF datasets and explore results.</p>
        </div>

        {/* Action cards */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          <ActionCard
            onClick={() => setShowDatasetsDrawer(true)}
            cardBg="#eff6ff"
            iconBg="#dbeafe"
            icon={<Database style={{ width: 15, height: 15, color: '#3b82f6' }} />}
            title="Datasets"
            badge={String(datasets.length)}
            badgeStyle={{ background: '#bfdbfe', color: '#1d4ed8' }}
            description="Manage PDF collections used as input for analytics runs."
            actionLabel="Manage"
          />
          <ActionCard
            onClick={() => setShowNewRunModal(true)}
            cardBg="#f0fdf4"
            iconBg="#dcfce7"
            icon={<Play style={{ width: 15, height: 15, color: '#16a34a' }} />}
            title="New Run"
            description="Trigger an analytics run against a dataset with a selected config."
            actionLabel="Create Run"
          />
          <ActionCard
            onClick={() => {}}
            disabled
            iconBg="rgba(255,255,255,.15)"
            cardBg="#4f6ef7"
            icon={<Sparkles style={{ width: 14, height: 14, color: '#fff' }} />}
            title="Ask AI"
            titleColor="#fff"
            description="Select completed runs and compare results using AI chat."
            descriptionColor="rgba(255,255,255,.7)"
            badge="Coming Soon"
            badgeStyle={{ background: 'rgba(255,255,255,.2)', color: '#fff' }}
          />
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Dropdown>
            <DropdownTrigger variant="outline" style={{ fontSize: 13, minWidth: 160 }}>
              {filterDataset ? (datasets.find(d => d.id === filterDataset)?.name || 'All Datasets') : 'All Datasets'}
            </DropdownTrigger>
            <DropdownContent>
              <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                <DropdownItem onSelect={() => handleFilterDataset('')}>All Datasets</DropdownItem>
                {datasets.map(d => <DropdownItem key={d.id} onSelect={() => handleFilterDataset(d.id)}>{d.name}</DropdownItem>)}
              </div>
            </DropdownContent>
          </Dropdown>
          <Dropdown>
            <DropdownTrigger variant="outline" style={{ fontSize: 13, minWidth: 130 }}>
              {filterStatus ? filterStatus.charAt(0) + filterStatus.slice(1).toLowerCase() : 'All Statuses'}
            </DropdownTrigger>
            <DropdownContent>
              <DropdownItem onSelect={() => handleFilterStatus('')}>All Statuses</DropdownItem>
              <DropdownItem onSelect={() => handleFilterStatus('COMPLETED')}>Completed</DropdownItem>
              <DropdownItem onSelect={() => handleFilterStatus('PROCESSING')}>Processing</DropdownItem>
              <DropdownItem onSelect={() => handleFilterStatus('PARTIAL')}>Partial</DropdownItem>
              <DropdownItem onSelect={() => handleFilterStatus('FAILED')}>Failed</DropdownItem>
            </DropdownContent>
          </Dropdown>
          {/* Transaction ref search — press Enter to apply, X to clear */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search style={{ position: 'absolute', left: 8, width: 13, height: 13, color: '#9ca3af', pointerEvents: 'none' }} />
            <input
              style={filterInputStyle}
              placeholder="Tracking ID"
              value={txnRefDraft}
              onChange={e => setTxnRefDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearchTxnRef(txnRefDraft); }}
            />
            {txnRefDraft && (
              <button
                onClick={() => { setTxnRefDraft(''); handleSearchTxnRef(''); }}
                style={{ position: 'absolute', right: 6, background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: '#9ca3af' }}
                title="Clear"
              >
                <X style={{ width: 12, height: 12 }} />
              </button>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw style={{ width: 12, height: 12, animation: refreshing ? 'sb-spin 0.7s linear infinite' : 'none' }} />
            Refresh
          </Button>
        </div>

        {/* Runs table */}
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e8ecf4' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '30px 260px 140px 1fr 110px 90px 110px 130px', borderBottom: '1px solid #f1f5f9', padding: '0 14px' }}>
            <span style={{ padding: '10px 4px' }} />
            {['Run ID', 'Tracking ID', 'Dataset', 'Status', 'PDFs', 'Created', ''].map((h, i) => (
              <span key={i} style={{ fontSize: 11.5, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em', padding: '10px 4px' }}>{h}</span>
            ))}
          </div>

          {runs.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
              No runs found. Trigger a new run to get started.
            </div>
          ) : runs.map(run => {
            const isExpanded = expandedRuns.has(run.id);
            const isLoading  = runResultsLoading.has(run.id);
            const results    = runResults[run.id] || [];
            const stClass     = STATUS_TAG_CLASS[run.status] || 'bg-slate-100 text-slate-500 rounded-full';
            const pdfText    = run.totalItems == null ? '—' : `${run.completed} / ${run.totalItems}`;
            const pdfColor   = run.status === 'COMPLETED' ? '#15803d' : run.status === 'FAILED' ? '#dc2626' : '#9ca3af';

            return (
              <div key={run.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                {/* Row */}
                <div
                  onClick={() => toggleExpandRun(run.id)}
                  style={{ display: 'grid', gridTemplateColumns: '30px 260px 140px 1fr 110px 90px 110px 130px', alignItems: 'center', padding: '0 14px', cursor: 'pointer', transition: 'background .1s' }}
                  onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = '#fafbff'}
                  onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = 'transparent'}
                >
                  <div style={{ padding: '12px 0', display: 'flex', alignItems: 'center' }}>
                    <ChevronRight style={{ width: 14, height: 14, color: '#9ca3af', transition: 'transform .15s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }} />
                  </div>
                  <div style={{ padding: '12px 4px', minWidth: 0 }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{run.id}</div>
                  </div>
                  <div style={{ padding: '12px 4px', minWidth: 0 }}>
                    {run.transactionRef
                      ? <span title={run.transactionRef} style={{ fontFamily: 'monospace', fontSize: 11.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{run.transactionRef}</span>
                      : <span style={{ fontSize: 12, color: '#d1d5db' }}>—</span>}
                  </div>
                  <span style={{ fontSize: 13.5, color: '#374151', padding: '12px 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{run.datasetName}</span>
                  <div style={{ padding: '12px 4px' }}>
                    <Tag size="sm" className={stClass}>{run.status}</Tag>
                  </div>
                  <div style={{ fontSize: 13.5, fontWeight: 500, color: pdfColor, padding: '12px 4px' }}>{pdfText}</div>
                  <div style={{ fontSize: 13, color: '#6b7280', padding: '12px 4px' }}>{run.createdAt}</div>
                  {/* Actions — stop propagation so clicking doesn't toggle expand */}
                  <div style={{ padding: '12px 6px 12px 4px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }} onClick={e => e.stopPropagation()}>
                    {(run.status === 'COMPLETED' || run.status === 'PARTIAL') && journeys.zip_download_enabled !== false && (
                      <button
                        disabled={!!runDownloading[run.id]}
                        onClick={() => downloadRunZIP(
                          run.id,
                          (v) => setRunDownloading(prev => ({ ...prev, [run.id]: v })),
                          (v) => setRunDownloadError(prev => ({ ...prev, [run.id]: v }))
                        )}
                        style={{ background: '#fff', color: runDownloading[run.id] ? '#9ca3af' : '#374151', padding: '5px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: runDownloading[run.id] ? 'not-allowed' : 'pointer', border: '1px solid #d1d5db', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
                        onMouseEnter={e => { if (!runDownloading[run.id]) (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
                      >
                        <Download style={{ width: 11, height: 11 }} />
                        {runDownloading[run.id] ? 'Preparing…' : 'Download All'}
                      </button>
                    )}
                    {runDownloadError[run.id] && (
                      <span style={{ fontSize: 10, color: '#dc2626' }}>{runDownloadError[run.id]}</span>
                    )}
                  </div>
                </div>

                {/* Inline expansion */}
                {isExpanded && (
                  <div style={{ background: '#f8fafc', borderTop: '1px solid #f1f5f9', padding: '12px 14px 14px 44px' }}>
                    {isLoading ? (
                      <div style={{ padding: '14px 0', display: 'flex', justifyContent: 'center' }}>
                        <svg style={{ width: 20, height: 20, animation: 'sb-spin 0.7s linear infinite', color: '#4f6ef7' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                      </div>
                    ) : run.status === 'PROCESSING' && results.length === 0 ? (
                      <div style={{ padding: '12px 0', fontSize: 13, color: '#9ca3af' }}>Run in progress — results will appear when complete.</div>
                    ) : results.length === 0 ? (
                      <div style={{ padding: '14px 0', textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>No results available for this run.</div>
                    ) : (
                      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf4', overflow: 'hidden' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 90px 36px', borderBottom: '1px solid #f1f5f9', padding: '0 12px' }}>
                          {['Source', 'Journey', 'Status', ''].map(h => (
                            <span key={h} style={{ fontSize: 10.5, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em', padding: '8px 4px' }}>{h}</span>
                          ))}
                        </div>
                        {results.map((item, i) => {
                          const pbClass = PDF_STATUS_TAG_CLASS[item.status] || 'bg-slate-100 text-slate-500 rounded-full';
                          const isConsent = item.item_type === 'CONSENT';
                          const iconColor = isConsent ? '#60a5fa' : '#f87171';
                          return (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 90px 36px', alignItems: 'center', borderBottom: i < results.length - 1 ? '1px solid #f9fafb' : 'none', padding: '0 12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 4px', minWidth: 0 }}>
                                {isConsent ? (
                                  <svg viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth={2} style={{ width: 12, height: 12, flexShrink: 0 }}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                ) : (
                                  <svg viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth={2} style={{ width: 12, height: 12, flexShrink: 0 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                )}
                                <span style={{ fontSize: 12.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.source_ref}</span>
                              </div>
                              <div style={{ padding: '9px 4px' }}>
                                {item.journey_id ? (
                                  <span style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{item.journey_id}</span>
                                ) : <span style={{ fontSize: 11, color: '#d1d5db' }}>—</span>}
                              </div>
                              <div style={{ padding: '9px 4px' }}>
                                <Tag size="sm" className={pbClass}>{item.status}</Tag>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '9px 4px' }}>
                                {item.status === 'SUCCESS' && (item.json_s3_url || (item.json_results && item.json_results.length > 0)) && (
                                  <button
                                    title="Download JSON"
                                    onClick={() => {
                                      if (item.json_s3_url) {
                                        const a = document.createElement('a');
                                        a.href = item.json_s3_url;
                                        a.click();
                                      } else {
                                        item.json_results!.forEach(r => {
                                          const a = document.createElement('a');
                                          a.href = r.url;
                                          a.click();
                                        });
                                      }
                                    }}
                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'inline-flex', color: '#6b7280' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#374151'; (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#6b7280'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                                  >
                                    <Download style={{ width: 12, height: 12 }} />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {(runResultsTotals[run.id] ?? 0) > RESULTS_PAGE_SIZE && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderTop: '1px solid #f1f5f9' }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                          Page {runResultsPages[run.id] ?? 1} of {Math.ceil((runResultsTotals[run.id] ?? 0) / RESULTS_PAGE_SIZE)}
                        </span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Button variant="secondary" size="sm" disabled={(runResultsPages[run.id] ?? 1) <= 1} onClick={() => loadRunResultsPage(run.id, (runResultsPages[run.id] ?? 1) - 1)}>← Prev</Button>
                          <Button variant="secondary" size="sm" disabled={(runResultsPages[run.id] ?? 1) >= Math.ceil((runResultsTotals[run.id] ?? 0) / RESULTS_PAGE_SIZE)} onClick={() => loadRunResultsPage(run.id, (runResultsPages[run.id] ?? 1) + 1)}>Next →</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination footer */}
          {totalRuns > 0 && (() => {
            const totalPages = Math.max(1, Math.ceil(totalRuns / PAGE_SIZE));
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', borderTop: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 13, color: '#6b7280' }}>
                  {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalRuns)} of {totalRuns} runs
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button
                    variant="secondary" size="sm"
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    ← Prev
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
                      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, i) =>
                      p === '…' ? (
                        <span key={`e${i}`} style={{ padding: '4px 6px', fontSize: 13, color: '#9ca3af' }}>…</span>
                      ) : (
                        <Button
                          key={p}
                          onClick={() => handlePageChange(p as number)}
                          variant={currentPage === p ? 'primary' : 'secondary'} size="sm"
                        >
                          {p}
                        </Button>
                      )
                    )}
                  <Button
                    variant="secondary" size="sm"
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Datasets Drawer */}
      <SandboxDatasetsDrawer
        open={showDatasetsDrawer}
        datasets={datasets}
        onClose={() => setShowDatasetsDrawer(false)}
        onNewDataset={() => { setShowNewDatasetModal(true); }}
        onUploadFiles={handleUploadFilesForDataset}
        onDeleteDataset={handleDeleteDataset}
        onDeleteFile={handleDeleteFile}
        onLoadFiles={handleLoadDatasetFiles}
        onAddConsents={handleAddConsents}
        onRemoveConsent={handleRemoveConsent}
        maxPdfItems={maxPdfItems}
        maxConsentItems={maxConsentItems}
      />

      {/* ── New Run Modal ──────────────────────────────────── */}
      <Modal open={showNewRunModal} onClose={() => { setShowNewRunModal(false); setRunPdfJourneyId(''); setRunConsentJourneyId(''); setRunDatasetDetail(null); setRunFieldErrors({ dataset: false, journey: false }); setRunTransactionRef(''); }} width={480}>
        <ModalHeader title="New Run" onClose={() => { setShowNewRunModal(false); setRunPdfJourneyId(''); setRunConsentJourneyId(''); setRunDatasetDetail(null); setRunFieldErrors({ dataset: false, journey: false }); setRunTransactionRef(''); }} />
        <div style={{ padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <FormField label="Dataset" required error={runFieldErrors.dataset ? 'This field is required' : undefined}>
            <Dropdown>
              <DropdownTrigger variant="outline" style={{ width: '100%', justifyContent: 'space-between', fontSize: 13, borderColor: runFieldErrors.dataset ? '#ef4444' : undefined }}>
                {runDatasetId ? datasets.find(d => d.id === runDatasetId)?.name || 'Select a dataset…' : 'Select a dataset…'}
              </DropdownTrigger>
              <DropdownContent zIndex={1300}>
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                {datasets.map(d => (
                  <DropdownItem key={d.id} onSelect={() => {
                    setRunDatasetId(d.id);
                    setRunPdfJourneyId('');
                    setRunConsentJourneyId('');
                    setRunFieldErrors({ dataset: false, journey: false });
                    setRunDatasetDetail({ files: d.files.map(f => f.name), consents: d.consents });
                  }}>
                    {d.name} ({d.fileCount ?? d.files.length} PDFs · {d.consents.length} consents)
                  </DropdownItem>
                ))}
                </div>
              </DropdownContent>
            </Dropdown>
          </FormField>

          <InputField
            label="Tracking ID"
            size="sm"
            value={runTransactionRef}
            onChange={e => setRunTransactionRef(e.target.value)}
            placeholder="e.g. TXN-2024-001"
          />

          {/* ── Journey selection ── */}
          {runDatasetDetail && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {selectedDsHasPdfs && (
                <FormField label={`PDF Journey (${runDatasetDetail.files.length} file${runDatasetDetail.files.length !== 1 ? 's' : ''})`} required error={runFieldErrors.journey && !runPdfJourneyId ? 'Required' : undefined}>
                  <Dropdown>
                    <DropdownTrigger variant="outline" style={{ width: '100%', justifyContent: 'space-between', fontSize: 13, borderColor: runFieldErrors.journey && !runPdfJourneyId ? '#ef4444' : undefined }}>
                      {runPdfJourneyId || 'Select journey…'}
                    </DropdownTrigger>
                    <DropdownContent zIndex={1300}>
                      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                        {journeys.pdf.map(j => (
                          <DropdownItem key={j.journey_id} onSelect={() => { setRunPdfJourneyId(j.journey_id); setRunFieldErrors(p => ({ ...p, journey: false })); }}>
                            {j.journey_id}
                          </DropdownItem>
                        ))}
                      </div>
                    </DropdownContent>
                  </Dropdown>
                </FormField>
              )}

              {selectedDsHasConsents && (
                <FormField label={`Consent Journey (${runDatasetDetail.consents.length} consent${runDatasetDetail.consents.length !== 1 ? 's' : ''})`} required error={runFieldErrors.journey && !runConsentJourneyId ? 'Required' : undefined}>
                  <Dropdown>
                    <DropdownTrigger variant="outline" style={{ width: '100%', justifyContent: 'space-between', fontSize: 13, borderColor: runFieldErrors.journey && !runConsentJourneyId ? '#ef4444' : undefined }}>
                      {runConsentJourneyId || 'Select journey…'}
                    </DropdownTrigger>
                    <DropdownContent zIndex={1300}>
                      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                        {journeys.consent.map(j => (
                          <DropdownItem key={j.journey_id} onSelect={() => { setRunConsentJourneyId(j.journey_id); setRunFieldErrors(p => ({ ...p, journey: false })); }}>
                            {j.journey_id}
                          </DropdownItem>
                        ))}
                      </div>
                    </DropdownContent>
                  </Dropdown>
                </FormField>
              )}

              {!selectedDsHasPdfs && !selectedDsHasConsents && (
                <div style={{ fontSize: 12.5, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '7px 10px' }}>
                  This dataset has no PDFs or consents. Add content before creating a run.
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 13px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: '#374151' }}>Custom Attributes</div>
              <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 1 }}>Configure your own journey ID with custom attributes</div>
            </div>
            <Button variant="secondary" size="sm" onClick={() => { setShowNewRunModal(false); navigateToOnboarding(); }} style={{ whiteSpace: 'nowrap' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 11, height: 11 }}><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/></svg>
              Configure
            </Button>
          </div>

          {triggerRunError && !showRunConfirmModal && (
            <div style={{ fontSize: 12.5, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '7px 10px' }}>
              {triggerRunError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 2 }}>
            <Button variant="secondary" size="sm" onClick={() => { setShowNewRunModal(false); setRunPdfJourneyId(''); setRunConsentJourneyId(''); setRunDatasetDetail(null); setRunFieldErrors({ dataset: false, journey: false }); setRunTransactionRef(''); setTriggerRunError(null); }}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleTriggerRun}>
              <Play style={{ width: 13, height: 13 }} />
              Trigger Run
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Trigger Run Confirm Modal ──────────────────────── */}
      <Modal open={showRunConfirmModal} onClose={() => { setShowRunConfirmModal(false); setTriggerRunError(null); }} width={360}>
        <div style={{ padding: '22px 20px 18px' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 6 }}>Trigger Analytics Run?</div>
          <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, marginBottom: triggerRunError ? 10 : 20 }}>
            Are you sure you want to start this run? This cannot be undone once started.
          </div>
          {triggerRunError && (
            <div style={{ fontSize: 12.5, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '7px 10px', marginBottom: 14 }}>
              {triggerRunError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" size="sm" onClick={() => { setShowRunConfirmModal(false); setTriggerRunError(null); }}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={confirmTriggerRun}>
              <Play style={{ width: 13, height: 13 }} />
              Yes, Trigger
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── New Dataset Modal ──────────────────────────────── */}
      <Modal open={showNewDatasetModal} onClose={() => { setShowNewDatasetModal(false); setNewDatasetName(''); setNewDatasetFiles([]); setNewDatasetPasswords({}); setNewDsProtectedFiles(new Set()); setNewDatasetConsentInput(''); setCreateError(null); }} width={500}>
        <ModalHeader title="Create New Dataset" onClose={() => { setShowNewDatasetModal(false); setNewDatasetName(''); setNewDatasetFiles([]); setNewDatasetPasswords({}); setNewDsProtectedFiles(new Set()); setNewDatasetConsentInput(''); setCreateError(null); }} />
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <InputField
            label="Name"
            required
            size="sm"
            value={newDatasetName}
            onChange={e => setNewDatasetName(e.target.value)}
            placeholder="e.g. Salaried Employees Q2"
          />

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
              Upload PDFs <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(optional — add files later)</span>
            </label>
            <div
              onClick={() => newDsFileInputRef.current?.click()}
              style={{ border: '2px dashed #c7d2fe', borderRadius: 9, padding: 24, textAlign: 'center', background: '#f8f9ff', cursor: 'pointer' }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#4f6ef7'; (e.currentTarget as HTMLDivElement).style.background = '#f0f4ff'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#c7d2fe'; (e.currentTarget as HTMLDivElement).style.background = '#f8f9ff'; }}
            >
              <Upload style={{ width: 32, height: 32, margin: '0 auto 8px', color: '#4f6ef7', strokeWidth: 1.5 }} />
              <p style={{ fontSize: 13, fontWeight: 500, color: '#374151', margin: 0 }}>Drop PDF files here or <span style={{ color: '#4f6ef7' }}>browse</span></p>
              <p style={{ fontSize: 11.5, color: '#9ca3af', margin: '4px 0 0' }}>PDF files only · max {maxPdfItems} per run</p>
              <input ref={newDsFileInputRef} type="file" multiple accept=".pdf" style={{ display: 'none' }}
                onChange={async e => {
                  const newFiles = Array.from(e.target.files || []);
                  if (!newFiles.length) return;
                  setNewDatasetFiles(prev => {
                    const existing = new Set(prev.map(f => f.name));
                    return [...prev, ...newFiles.filter(f => !existing.has(f.name))];
                  });
                  const newProtected = await detectProtectedFiles(newFiles);
                  setNewDsProtectedFiles(prev => new Set([...prev, ...newProtected]));
                  e.target.value = '';
                }} />
            </div>
            {newDatasetFiles.length > 0 && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                {newDatasetFiles.map((f, i) => (
                  <div key={i} style={{ padding: '8px 10px', background: '#f9fafb', borderRadius: 7, border: '1px solid #f1f5f9' }}>
                    {/* File row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth={2} style={{ width: 13, height: 13, flexShrink: 0 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <span style={{ fontSize: 12.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                        <button onClick={() => {
                            const name = newDatasetFiles[i].name;
                            setNewDatasetFiles(prev => prev.filter((_, j) => j !== i));
                            setNewDsProtectedFiles(prev => { const s = new Set(prev); s.delete(name); return s; });
                          }}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'inline-flex', color: '#9ca3af' }}>
                          <X style={{ width: 12, height: 12 }} />
                        </button>
                      </div>
                    </div>
                    {/* Password row — only shown if PDF is detected as encrypted */}
                    {newDsProtectedFiles.has(f.name) && (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Lock style={{ width: 11, height: 11, color: '#f59e0b', flexShrink: 0 }} />
                        <input
                          className="sb-pwd-field"
                          type={newDsPwdVisible.has(f.name) ? 'text' : 'password'}
                          placeholder="Password required"
                          value={newDatasetPasswords[f.name] || ''}
                          onChange={e => setNewDatasetPasswords(prev => ({ ...prev, [f.name]: e.target.value }))}
                          style={pwdInputStyle}
                        />
                        <button type="button" onClick={() => setNewDsPwdVisible(prev => { const s = new Set(prev); s.has(f.name) ? s.delete(f.name) : s.add(f.name); return s; })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: '#9ca3af', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {newDsPwdVisible.has(f.name) ? <EyeOff style={{ width: 13, height: 13 }} /> : <Eye style={{ width: 13, height: 13 }} />}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
              Consent IDs <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(optional — separate by comma or newline)</span>
            </label>
            <textarea
              className="sb-input"
              rows={3}
              placeholder="e.g. consent-id-001, consent-id-002"
              value={newDatasetConsentInput}
              onChange={e => setNewDatasetConsentInput(e.target.value)}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          {createError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, color: '#dc2626' }}>
              {createError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 2 }}>
            <Button variant="secondary" size="sm" onClick={() => { setShowNewDatasetModal(false); setNewDatasetName(''); setNewDatasetFiles([]); setNewDatasetPasswords({}); setNewDsProtectedFiles(new Set()); setNewDatasetConsentInput(''); setCreateError(null); }}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleCreateDataset}>Create Dataset</Button>
          </div>
        </div>
      </Modal>

      {/* ── Upload PDFs to existing dataset ───────────────── */}
      <Modal open={showUploadModal} onClose={() => { setShowUploadModal(false); setUploadPasswords({}); setUploadProtectedFiles(new Set()); setUploadError(null); }} width={480}>
        <ModalHeader title="Upload PDFs" onClose={() => { setShowUploadModal(false); setUploadPasswords({}); setUploadProtectedFiles(new Set()); setUploadError(null); }} />
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 13 }}>
          <div
            onClick={() => uploadFileInputRef.current?.click()}
            style={{ border: '2px dashed #c7d2fe', borderRadius: 9, padding: 24, textAlign: 'center', background: '#f8f9ff', cursor: 'pointer' }}
            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#4f6ef7'; (e.currentTarget as HTMLDivElement).style.background = '#f0f4ff'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#c7d2fe'; (e.currentTarget as HTMLDivElement).style.background = '#f8f9ff'; }}
          >
            <Upload style={{ width: 36, height: 36, margin: '0 auto 8px', color: '#4f6ef7', strokeWidth: 1.5 }} />
            <p style={{ fontSize: 13, fontWeight: 500, color: '#374151', margin: 0 }}>Drop PDF files here or <span style={{ color: '#4f6ef7' }}>browse</span></p>
            <p style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 3 }}>PDF files only</p>
            <input ref={uploadFileInputRef} type="file" multiple accept=".pdf" style={{ display: 'none' }}
              onChange={async e => {
                const newFiles = Array.from(e.target.files || []);
                if (!newFiles.length) return;
                setUploadFiles(prev => {
                  const existing = new Set(prev.map(f => f.name));
                  return [...prev, ...newFiles.filter(f => !existing.has(f.name))];
                });
                const newProtected = await detectProtectedFiles(newFiles);
                setUploadProtectedFiles(prev => new Set([...prev, ...newProtected]));
                e.target.value = '';
              }} />
          </div>
          {uploadFiles.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {uploadFiles.map((f, i) => (
                <div key={i} style={{ padding: '8px 10px', background: '#f9fafb', borderRadius: 7, border: '1px solid #f1f5f9' }}>
                  {/* File row */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth={2} style={{ width: 13, height: 13, flexShrink: 0 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <span style={{ fontSize: 12.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                      <button
                        onClick={() => {
                          const name = uploadFiles[i].name;
                          setUploadFiles(prev => prev.filter((_, j) => j !== i));
                          setUploadProtectedFiles(prev => { const s = new Set(prev); s.delete(name); return s; });
                        }}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'inline-flex', color: '#9ca3af' }}
                      >
                        <X style={{ width: 12, height: 12 }} />
                      </button>
                    </div>
                  </div>
                  {/* Password row — only shown if PDF is detected as encrypted */}
                  {uploadProtectedFiles.has(f.name) && (
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Lock style={{ width: 11, height: 11, color: '#f59e0b', flexShrink: 0 }} />
                      <input
                        className="sb-pwd-field"
                        type={uploadPwdVisible.has(f.name) ? 'text' : 'password'}
                        placeholder="Password required"
                        value={uploadPasswords[f.name] || ''}
                        onChange={e => setUploadPasswords(prev => ({ ...prev, [f.name]: e.target.value }))}
                        style={pwdInputStyle}
                      />
                      <button type="button" onClick={() => setUploadPwdVisible(prev => { const s = new Set(prev); s.has(f.name) ? s.delete(f.name) : s.add(f.name); return s; })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: '#9ca3af', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {uploadPwdVisible.has(f.name) ? <EyeOff style={{ width: 13, height: 13 }} /> : <Eye style={{ width: 13, height: 13 }} />}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {uploadError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, color: '#dc2626' }}>
              {uploadError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" size="sm" onClick={() => { setShowUploadModal(false); setUploadPasswords({}); setUploadProtectedFiles(new Set()); setUploadError(null); }}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleConfirmUpload}>
              Upload {uploadFiles.length > 0 ? `${uploadFiles.length} file${uploadFiles.length > 1 ? 's' : ''}` : 'files'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Delete Dataset Confirm ─────────────────────────── */}
      <Modal open={showDeleteDatasetModal} onClose={() => { setShowDeleteDatasetModal(false); setPendingDeleteDataset(null); setDeleteDatasetError(null); }} width={400}>
        <div style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Trash2 style={{ width: 15, height: 15, color: '#ef4444' }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Delete Dataset?</div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>This action cannot be undone.</div>
            </div>
          </div>
          {pendingDeleteDataset && (
            <div style={{ background: '#fef2f2', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 12.5, color: '#b91c1c' }}>
              <strong>{pendingDeleteDataset.name}</strong> will be permanently deleted including:
              <ul style={{ listStyle: 'disc', marginLeft: 16, marginTop: 5, lineHeight: 1.8 }}>
                <li>{pendingDeleteDataset.fileCount ?? pendingDeleteDataset.files.length} PDF file{(pendingDeleteDataset.fileCount ?? pendingDeleteDataset.files.length) !== 1 ? 's' : ''}</li>
                <li>All associated runs and results</li>
                <li>All analytics data for this dataset</li>
              </ul>
            </div>
          )}
          {deleteDatasetError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, color: '#dc2626', marginBottom: 14 }}>
              {deleteDatasetError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" size="sm" onClick={() => { setShowDeleteDatasetModal(false); setPendingDeleteDataset(null); setDeleteDatasetError(null); }}>Cancel</Button>
            <button className="sb-btn-danger" onClick={confirmDeleteDataset}>Delete Dataset</button>
          </div>
        </div>
      </Modal>
      {/* ── Delete File Confirm ───────────────────────────── */}
      <Modal open={showDeleteFileModal} onClose={() => { setShowDeleteFileModal(false); setPendingDeleteFile(null); setFileDeleteError(null); }} width={400}>
        <div style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Trash2 style={{ width: 15, height: 15, color: '#ef4444' }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Remove PDF?</div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>This file will be permanently removed from the dataset.</div>
            </div>
          </div>
          {pendingDeleteFile && (
            <div style={{ background: '#fef2f2', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 12.5, color: '#b91c1c' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 13, height: 13, flexShrink: 0 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pendingDeleteFile.file.name}</strong>
              </div>
              <div style={{ marginTop: 5, color: '#6b7280', fontWeight: 400 }}>from dataset <strong style={{ color: '#374151' }}>{pendingDeleteFile.datasetName}</strong></div>
            </div>
          )}
          {fileDeleteError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, color: '#dc2626', marginBottom: 14 }}>
              {fileDeleteError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" size="sm" onClick={() => { setShowDeleteFileModal(false); setPendingDeleteFile(null); setFileDeleteError(null); }}>Cancel</Button>
            <button className="sb-btn-danger" onClick={confirmDeleteFile}>Remove File</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ── Small reusable helpers ─────────────────────────────────────────────────────

interface ActionCardProps {
  onClick: () => void;
  disabled?: boolean;
  iconBg: string;
  cardBg?: string;
  icon: React.ReactNode;
  title: string;
  titleColor?: string;
  description: string;
  descriptionColor?: string;
  badge?: string;
  badgeStyle?: React.CSSProperties;
  actionLabel?: string;
}

const ActionCard: React.FC<ActionCardProps> = ({
  onClick, disabled, iconBg, cardBg = '#fff', icon, title, titleColor = '#111827',
  description, descriptionColor = '#64748b', badge, badgeStyle, actionLabel,
}) => {
  const [hovered, setHovered] = useState(false);
  const isBlue = cardBg === '#4f6ef7';

  return (
    <div
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: isBlue ? '#4f6ef7' : '#fff',
        border: `1px solid ${isBlue ? 'transparent' : '#e2e8f0'}`,
        borderRadius: 12,
        cursor: disabled ? 'default' : 'pointer',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: hovered && !disabled
          ? (isBlue ? '0 8px 24px rgba(79,110,247,.4)' : '0 6px 20px rgba(0,0,0,.09)')
          : (isBlue ? '0 2px 8px rgba(79,110,247,.2)' : '0 1px 3px rgba(0,0,0,.04)'),
        transition: 'box-shadow .18s, transform .18s',
        transform: hovered && !disabled ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10, background: iconBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            boxShadow: isBlue ? 'none' : '0 1px 3px rgba(0,0,0,.08)',
          }}>
            {icon}
          </div>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: titleColor, letterSpacing: '-.01em' }}>{title}</span>
          {badge && (
            <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 10, ...badgeStyle }}>{badge}</span>
          )}
        </div>

        {/* Description */}
        <p style={{ fontSize: 12.5, color: descriptionColor, lineHeight: 1.55, margin: 0, flex: 1 }}>{description}</p>
      </div>

      {/* Footer */}
      <div style={{
        padding: '0 18px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: disabled ? 'flex-start' : 'flex-end',
      }}>
        {disabled ? (
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', fontStyle: 'italic' }}>Available very soon</span>
        ) : actionLabel ? (
          <Button
            variant="primary"
            size="sm"
            onClick={e => { e.stopPropagation(); onClick(); }}
          >
            {actionLabel}
            <ChevronRight style={{ width: 11, height: 11 }} />
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const Modal: React.FC<{ open: boolean; onClose: () => void; width?: number; children: React.ReactNode }> = ({ open, onClose, width = 480, children }) => (
  <div
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    style={{
      display: open ? 'flex' : 'none', position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,.4)', zIndex: 1202, alignItems: 'center', justifyContent: 'center',
    }}
  >
    <div style={{ background: '#fff', borderRadius: 13, width, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.15)' }}>
      {children}
    </div>
  </div>
);

const ModalHeader: React.FC<{ title: string; onClose: () => void }> = ({ title, onClose }) => (
  <div style={{ padding: '16px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{title}</span>
    <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 5, fontSize: 16, color: '#6b7280', lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}>✕</button>
  </div>
);

const FormField: React.FC<{ label: string; required?: boolean; error?: string; children: React.ReactNode }> = ({ label, required, error, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
      {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
    </label>
    {children}
    {error && <div style={{ fontSize: 11.5, color: '#ef4444', marginTop: 4 }}>{error}</div>}
  </div>
);

// ── Styles ─────────────────────────────────────────────────────────────────────

// Triple-class specificity (.sb-root.sb-root.sb-root) beats Bootstrap's class-based !important rules
// Same pattern used by Equal DS to guarantee style isolation in hybrid Angular/React apps
const SANDBOX_STYLES = `
  @keyframes sb-spin { to { transform: rotate(360deg); } }

  /* ── Textarea base styles ── */
  .sb-root.sb-root.sb-root textarea {
    border: 1px solid #d1d5db !important;
    border-radius: 6px !important;
    background: #ffffff !important;
    color: #374151 !important;
    box-shadow: none !important;
    outline: none !important;
    padding: 8px 10px !important;
    font-size: 12.5px !important;
    font-family: monospace !important;
    line-height: 1.6 !important;
    box-sizing: border-box !important;
    width: 100% !important;
    resize: vertical !important;
  }
  .sb-root.sb-root.sb-root textarea:focus {
    border-color: #6366f1 !important;
    box-shadow: 0 0 0 3px rgba(99,102,241,.1) !important;
    outline: none !important;
  }

  /* ── Password inputs (file list inline inputs) ── */
  .sb-root.sb-root.sb-root .sb-pwd-field {
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    background-color: #ffffff !important;
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 7px !important;
    padding: 7px 11px !important;
    font-size: 13px !important;
    height: auto !important;
    line-height: 1.5 !important;
    box-shadow: none !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    color: #374151 !important;
    outline: none !important;
  }
  .sb-root.sb-root.sb-root .sb-pwd-field:focus {
    border-color: #4f6ef7 !important;
    box-shadow: 0 0 0 3px rgba(79,110,247,.10) !important;
    outline: none !important;
  }

  /* ── Placeholder text visibility ── */
  .sb-root.sb-root.sb-root input::placeholder,
  .sb-root.sb-root.sb-root textarea::placeholder {
    color: #94a3b8 !important;
    opacity: 1 !important;
  }

  /* ── Consent section buttons ── */
  .sb-root.sb-root.sb-root button.sb-consent-add-btn {
    display: inline-flex !important;
    align-items: center !important;
    gap: 5px !important;
    font-size: 12px !important;
    font-weight: 500 !important;
    padding: 5px 11px !important;
    border-radius: 6px !important;
    border: none !important;
    cursor: pointer !important;
    background: #d97706 !important;
    color: #fff !important;
    white-space: nowrap !important;
    line-height: 1.4 !important;
  }
  .sb-root.sb-root.sb-root button.sb-consent-cancel-btn {
    display: inline-flex !important;
    align-items: center !important;
    font-size: 12.5px !important;
    font-weight: 500 !important;
    padding: 5px 12px !important;
    border-radius: 6px !important;
    border: 1px solid #d1d5db !important;
    background: #fff !important;
    color: #374151 !important;
    cursor: pointer !important;
    line-height: 1.4 !important;
  }
  .sb-root.sb-root.sb-root button.sb-consent-submit-btn {
    display: inline-flex !important;
    align-items: center !important;
    font-size: 12.5px !important;
    font-weight: 500 !important;
    padding: 5px 12px !important;
    border-radius: 6px !important;
    border: none !important;
    background: #d97706 !important;
    color: #fff !important;
    line-height: 1.4 !important;
  }
  .sb-root.sb-root.sb-root button.sb-consent-submit-btn:disabled {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
  }

  /* ── Danger (destructive action) buttons ── */
  .sb-root.sb-root.sb-root button.sb-btn-danger {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 5px !important;
    cursor: pointer !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    padding: 7px 14px !important;
    border-radius: 6px !important;
    white-space: nowrap !important;
    border: none !important;
    background: #ef4444 !important;
    color: #fff !important;
    line-height: 1.4 !important;
  }
  .sb-root.sb-root.sb-root button.sb-btn-danger:hover {
    background: #dc2626 !important;
  }
  .sb-root.sb-root.sb-root button.sb-btn-danger:disabled {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
  }

`;

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1.5px solid #d1d5db',
  borderRadius: 7,
  padding: '7px 11px',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
  backgroundColor: '#ffffff',
  color: '#374151',
};


const pwdInputStyle: React.CSSProperties = {
  flex: 1,
  padding: '5px 9px',
  fontSize: 12,
  border: '1.5px solid #d1d5db',
  borderRadius: 5,
  backgroundColor: '#fff',
  color: '#374151',
  outline: 'none',
  minWidth: 0,
};
